import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
// import { TradingChart } from './trading-chart';
import { TradingViewChart } from './TradingViewChart';
import { pythNetwork } from '../services/pythNetwork';

interface Position {
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  currentPrice: number;
  pnl: number;
  pnlPercent: number;
}

const TradingPage: React.FC = () => {
  const [orderType, setOrderType] = useState<'market' | 'limit' | 'stop'>('market');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('ETH/USDT');
  const baseSymbol = useMemo(() => selectedSymbol.split('/')[0], [selectedSymbol]);
  const [livePrice, setLivePrice] = useState<number | null>(null);
  const [liveChange, setLiveChange] = useState<string>('');
  const [volumeHint, setVolumeHint] = useState<string>('—');

  const positions: Position[] = [
    {
      symbol: 'APT/USDT',
      side: 'long',
      size: 100,
      entryPrice: 8.15,
      currentPrice: 8.24,
      pnl: 9.00,
      pnlPercent: 1.10
    },
    {
      symbol: 'ETH/USDT',
      side: 'short',
      size: 0.5,
      entryPrice: 2465.00,
      currentPrice: 2450.00,
      pnl: 7.50,
      pnlPercent: 0.61
    }
  ];

  const symbols = ['ETH/USDT', 'BTC/USDT', 'SOL/USDT', 'HBAR/USDT', 'PYTH/USDT'];
  
  // Initialize optional feeds for HBAR and PYTH from env, if provided
  useEffect(() => {
    const env: any = (import.meta as any).env || {};
    if (env.VITE_PYTH_FEED_HBAR) {
      pythNetwork.addPriceFeed('HBAR', env.VITE_PYTH_FEED_HBAR);
    }
    if (env.VITE_PYTH_FEED_PYTH) {
      pythNetwork.addPriceFeed('PYTH', env.VITE_PYTH_FEED_PYTH);
    }
  }, []);

  // Fetch live market header data via Pyth + SSE
  useEffect(() => {
    let mounted = true;
    let unsub: (() => void) | null = null;
    (async () => {
      try {
        const p = await pythNetwork.getSinglePrice(baseSymbol);
        if (mounted && p) {
          setLivePrice(p.price);
          const change = p.priceChange;
          setLiveChange(`${change >= 0 ? '+' : ''}${change.toFixed(2)}%`);
          // set a simple volume hint depending on symbol
          const v = baseSymbol === 'BTC' ? '900M' : baseSymbol === 'ETH' ? '2.1B' : '—';
          setVolumeHint(v);
        }
      } catch {}
      // start stream
      unsub = pythNetwork.createPriceStream([baseSymbol], (prices) => {
        const price = prices?.[0]?.price;
        const ema = prices?.[0]?.emaPrice;
        if (!mounted || price == null || ema == null) return;
        setLivePrice(price);
        const change = ((price - ema) / (ema || price)) * 100;
        setLiveChange(`${change >= 0 ? '+' : ''}${change.toFixed(2)}%`);
      });
    })();
    return () => {
      mounted = false;
      if (unsub) unsub();
    };
  }, [baseSymbol]);

  const handlePlaceOrder = () => {
    console.log('Placing order:', { orderType, side, amount, price, symbol: selectedSymbol });
    // Reset form
    setAmount('');
    setPrice('');
  };

  const getPnlColor = (pnl: number) => {
    return pnl >= 0 ? 'text-green-400' : 'text-red-400';
  };

  return (
    <div className="h-full bg-[#0a0a1a] text-green-400 font-mono flex">
      {/* Left Panel - Trading Interface */}
      <div className="w-80 bg-[#0d0d2a] border-r border-green-500/30 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-green-500/30">
          <h2 className="text-lg font-bold text-green-400 mb-2">Quick Trade</h2>
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            className="w-full bg-transparent border border-green-500/30 text-green-400 text-sm rounded px-3 py-2 focus:border-green-400 focus:outline-none"
          >
            {symbols.map(symbol => (
              <option key={symbol} value={symbol} className="bg-[#0d0d2a]">{symbol}</option>
            ))}
          </select>
        </div>

        {/* Order Form */}
        <div className="p-4 flex-1">
          {/* Order Type */}
          <div className="mb-4">
            <div className="text-sm text-green-400/70 mb-2">Order Type</div>
            <div className="flex space-x-1">
              {['market', 'limit', 'stop'].map((type) => (
                <Button
                  key={type}
                  onClick={() => setOrderType(type as any)}
                  className={`flex-1 text-xs ${
                    orderType === type
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-transparent text-green-400/70 hover:bg-green-500/10'
                  }`}
                >
                  {type.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          {/* Buy/Sell */}
          <div className="mb-4">
            <div className="flex space-x-1">
              <Button
                onClick={() => setSide('buy')}
                className={`flex-1 ${
                  side === 'buy'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-transparent text-green-400/70 hover:bg-green-500/10'
                }`}
              >
                BUY
              </Button>
              <Button
                onClick={() => setSide('sell')}
                className={`flex-1 ${
                  side === 'sell'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-transparent text-green-400/70 hover:bg-red-500/10'
                }`}
              >
                SELL
              </Button>
            </div>
          </div>

          {/* Amount */}
          <div className="mb-4">
            <div className="text-sm text-green-400/70 mb-2">Amount</div>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="bg-transparent border-green-500/30 text-green-400 placeholder-green-400/50 focus:border-green-400"
            />
            <div className="flex space-x-1 mt-2">
              {['25%', '50%', '75%', '100%'].map((percent) => (
                <Button
                  key={percent}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs border-green-500/30 text-green-400/70 hover:bg-green-500/10"
                >
                  {percent}
                </Button>
              ))}
            </div>
          </div>

          {/* Price (for limit/stop orders) */}
          {orderType !== 'market' && (
            <div className="mb-4">
              <div className="text-sm text-green-400/70 mb-2">Price</div>
              <Input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                className="bg-transparent border-green-500/30 text-green-400 placeholder-green-400/50 focus:border-green-400"
              />
            </div>
          )}

          {/* Order Summary */}
          <div className="mb-4 p-3 bg-green-500/5 border border-green-500/20 rounded">
            <div className="text-xs text-green-400/70 mb-2">Order Summary</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-green-400/70">Side:</span>
                <span className={side === 'buy' ? 'text-green-400' : 'text-red-400'}>
                  {side.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-400/70">Type:</span>
                <span className="text-green-400">{orderType.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-400/70">Amount:</span>
                <span className="text-green-400">{amount || '0'}</span>
              </div>
              {orderType !== 'market' && (
                <div className="flex justify-between">
                  <span className="text-green-400/70">Price:</span>
                  <span className="text-green-400">${price || '0'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Place Order Button */}
          <Button
            onClick={handlePlaceOrder}
            className={`w-full py-3 font-semibold ${
              side === 'buy'
                ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30'
            }`}
          >
            {side === 'buy' ? 'BUY' : 'SELL'} {selectedSymbol.split('/')[0]}
          </Button>
        </div>

        {/* Account Info */}
        <div className="p-4 border-t border-green-500/30">
          <div className="text-sm text-green-400/70 mb-2">Account Balance</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-green-400/70">USDT:</span>
              <span className="text-green-400">$12,450.00</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400/70">Available:</span>
              <span className="text-green-400">$8,230.00</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-400/70">In Orders:</span>
              <span className="text-green-400">$4,220.00</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Market Data Header */}
        <div className="p-4 border-b border-green-500/30 bg-[#0d0d2a]">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-green-400">{selectedSymbol}</h1>
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-green-300 text-lg">
                  {livePrice != null ? `$${livePrice.toFixed(2)}` : '—'}
                </span>
                <span className={liveChange.startsWith('+') ? 'text-green-400' : 'text-red-400'}>
                  {liveChange || '—'}
                </span>
                <span className="text-green-400/70">Vol: {volumeHint}</span>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-green-400/70">Last Updated</div>
              <div className="text-green-400">{new Date().toLocaleTimeString()}</div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="flex-1 p-4">
          <TradingViewChart baseSymbol={baseSymbol} autosize />
        </div>

        {/* Positions */}
        <div className="p-4 border-t border-green-500/30 bg-[#0d0d2a]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-green-400 font-semibold">Open Positions</h3>
            <Button size="sm" variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10 text-xs">
              Close All
            </Button>
          </div>
          
          {positions.length > 0 ? (
            <div className="grid gap-3">
              {positions.map((position, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-500/5 border border-green-500/20 rounded">
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="text-green-300 font-medium">{position.symbol}</div>
                      <div className={`text-xs ${position.side === 'long' ? 'text-green-400' : 'text-red-400'}`}>
                        {position.side.toUpperCase()} {position.size}
                      </div>
                    </div>
                    <div className="text-sm">
                      <div className="text-green-400/70">Entry: ${position.entryPrice}</div>
                      <div className="text-green-400/70">Current: ${position.currentPrice}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`font-medium ${getPnlColor(position.pnl)}`}>
                      ${position.pnl.toFixed(2)}
                    </div>
                    <div className={`text-xs ${getPnlColor(position.pnl)}`}>
                      ({position.pnlPercent > 0 ? '+' : ''}{position.pnlPercent.toFixed(2)}%)
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs ml-4">
                    Close
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-green-400/50">
              No open positions
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingPage;
